import { Result } from "domain-entities";
import { useCallback, useEffect, useRef, useState } from "react";
import { firestore } from "firebase/app";
import { deleteUndefined } from "Helpers/FirestoreHelper";
import { useTranslation } from "Providers/TranslationProvider";

export interface DocumentStore<T> {
  isLoading: boolean;
  setItem: (item: T & ValidData, docId?: string) => Promise<void>;
  updateItem: (item: Partial<T & ValidData>, docId?: string) => Promise<void>;
  deleteItem: (item: T & ValidData) => Promise<void>;
  reset: () => void;
  item?: T;
  error?: Error;
  errorType?: "notExist";
}

export interface SaveDocumentStore<T> extends DocumentStore<T> {
  item: T;
}

export interface DocumentsOnceStore<T> {
  isLoading: boolean;
  items: T[];
}

export interface CollectionStore<T> {
  items: T[];
  isLoading: boolean;
  setItem: (item: T & ValidData) => Promise<void>;
  deleteItem: (item: T & ValidData) => Promise<void>;
  updateItem: (item: Partial<T> & ValidData) => Promise<void>;
  error?: Error;
}

export function isSaveDocumentStore<T>(
  documentStore: DocumentStore<T>
): documentStore is SaveDocumentStore<T> {
  return !!documentStore.item;
}

export function useDocumentStore<T>(props: {
  collection: firestore.CollectionReference<firestore.DocumentData>;
  check: (object: any) => Result;
  is: (object: any) => object is T;
  documentPath?: string;
}): DocumentStore<T> {
  const [item, setItem] = useState<T>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [errorType, setErrorType] = useState<"notExist">();
  const { t } = useTranslation();
  const is = useCallback(props.is, []);
  const check = useCallback(props.check, []);

  async function setFirebaseItem(item: T & ValidData, docId?: string) {
    await props.collection.doc(docId || item.id).set(deleteUndefined(item));
  }

  async function updateFirebaseItem(
    item: Partial<T & ValidData>,
    docId?: string
  ) {
    await props.collection
      .doc(docId || item.id)
      .update(deleteUndefined(item, true));
  }

  async function deleteFirebaseItem() {
    throw new Error("Delete is not allowed");
  }

  function reset() {
    setItem(undefined);
    setError(undefined);
    setErrorType(undefined);
    setIsLoading(true);
  }

  useEffect(() => {
    if (props.documentPath) {
      let documentRef = props.collection.doc(props.documentPath);
      let path = `collection("${props.collection.path}").doc("${props.documentPath}")`;

      const subscriber = documentRef.onSnapshot(
        async (doc) => {
          const docData = doc.data();

          if (!doc.exists) {
            setItem(undefined);
            if (doc.metadata.fromCache) {
              setError(new Error(t("Common_Error_Offline")));
            } else {
              setError(new Error(`${path} does not exist.`));
              setErrorType("notExist");
            }
          } else if (is(docData)) {
            setItem(docData);
            setError(undefined);
            setErrorType(undefined);
          } else {
            setItem(undefined);
            const result = check(docData);

            if (result.reasons?.length) {
              setError(
                new Error(
                  `${path} check error with reasons: ${result.reasons
                    .map((reason) => `${reason.path} ${reason.explanation}`)
                    .join(". ")}`
                )
              );
            } else {
              setError(new Error(`${path} check error without reasons.`));
            }
          }

          setIsLoading(false);
        },
        (error) => {
          setItem(undefined);
          error.message = `${path} error message: ${error.message}`;
          setError(error);
          setIsLoading(false);
        }
      );

      return subscriber;
    }
  }, [t, is, check, props.collection, props.documentPath]);

  return {
    item,
    isLoading,
    error,
    errorType,
    setItem: setFirebaseItem,
    updateItem: updateFirebaseItem,
    deleteItem: deleteFirebaseItem,
    reset,
  };
}

export interface ValidData {
  id?: string;
}

interface ValidFirestoreDocumentData extends firestore.DocumentData {
  id: string;
}

export type FirestoreWhere = [
  string | firestore.FieldPath,
  firestore.WhereFilterOp,
  any
];

export function useCollectionStore<T>(props: {
  collection: firestore.CollectionReference<firestore.DocumentData>;
  is: (object: any) => object is T;
  sort: (a: T, b: T) => number;
  where?: FirestoreWhere[];
  softDelete?: Partial<T>;
}): CollectionStore<T> {
  const [items, setItems] = useState<(T & ValidData)[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const is = useCallback(props.is, []);

  async function setFirebaseItem(item: T & ValidData) {
    await props.collection.doc(item.id).set(deleteUndefined(item));
  }

  async function updateFirebaseItem(item: Partial<T> & ValidData) {
    const nextItem = items.find((i) => i.id === item.id);

    if (nextItem) {
      Object.assign(nextItem, item);
      setItems([...items]);
    }

    await props.collection.doc(item.id).update(deleteUndefined(item, true));
  }

  async function deleteFirebaseItem(item: T & ValidData) {
    if (props.softDelete) {
      await props.collection.doc(item.id).set({
        ...deleteUndefined(item),
        ...props.softDelete,
      });
    } else {
      await props.collection.doc(item.id).delete();
    }
  }

  useEffect(() => {
    let collectionRef:
      | firestore.CollectionReference<firestore.DocumentData>
      | firestore.Query<firestore.DocumentData> = props.collection;
    let path = `collection("${props.collection.path}")`;
    props.where?.forEach((where) => {
      collectionRef = collectionRef.where(...where);
    });

    if (props.where?.length) {
      const whereStrings = props.where.map(
        (where) => `.where("${where[0]}", "${where[1]}", "${where[2]}")`
      );

      path += whereStrings.join("");
    }

    const subscriber = collectionRef.onSnapshot(
      (collection) => {
        const nextItems: (T & ValidData)[] = [];

        for (const doc of collection.docs) {
          const docData = doc.data();
          if (is(docData) && isValidFirestoreDocumentData(docData)) {
            nextItems.push(docData);
          }
        }

        setItems(nextItems.sort(props.sort));
        setError(undefined);
        setIsLoading(false);
      },
      (error) => {
        setItems([]);
        error.message = `${path} error message: ${error.message}`;
        setError(error);
        setIsLoading(false);
      }
    );

    return subscriber;
  }, [props.where, is, props.sort, props.collection]);

  return {
    items,
    isLoading,
    error,
    setItem: setFirebaseItem,
    updateItem: updateFirebaseItem,
    deleteItem: deleteFirebaseItem,
  };
}

export function isValidFirestoreDocumentData(
    documentData: firestore.DocumentData
): documentData is ValidFirestoreDocumentData {
  return !!documentData.id;
}

export function useDocumentsOnceStore<T>(props: {
  collection: firestore.CollectionReference<firestore.DocumentData>;
  documentPaths: string[];
  is: (object: any) => object is T;
  refreshTimeout?: number
}): DocumentsOnceStore<T> {
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<T[]>([]);
  const isMoutedRef = useRef(true);
  const interval = useRef<number>();
  const collectionRef = useRef(props.collection);
  const documentPathsRef = useRef(props.documentPaths);
  const documentPathsString = props.documentPaths.join("-");
  const is = useRef(props.is);
  const {refreshTimeout} = props;

  documentPathsRef.current = props.documentPaths;

  useEffect(() => {
    isMoutedRef.current = true;
    setItems([]);
    setIsLoading(true);

    if (interval.current) {
      clearInterval(interval.current);
    }

    if (refreshTimeout) {
      interval.current = window.setInterval(requestDocuments, refreshTimeout);
    }

    requestDocuments();

    async function requestDocuments() {
      const requests = documentPathsRef.current.map((documentPath) => {
        return async () => {
          const documentRef = collectionRef.current.doc(documentPath);

          const snapshot = await documentRef.get();
          const docData = snapshot.data();

          if (is.current(docData)) {
            return docData;
          }
        };
      });

      try {
        const results = await Promise.all(requests.map((request) => request()));

        if (
          isMoutedRef.current &&
          documentPathsString === documentPathsRef.current.join("-")
        ) {
          const validResults = results.reduce<T[]>((result, next) => {
            if (next !== undefined) {
              result.push(next);
            }

            return result;
          }, []);

          setIsLoading(false);
          setItems(validResults);
        }
      } catch (error) {
        if (
          isMoutedRef.current &&
          documentPathsString === documentPathsRef.current.join("-")
        ) {
          setIsLoading(false);
        }
      }
    }

    return () => {
      isMoutedRef.current = false;
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [documentPathsString]);

  return {
    isLoading,
    items,
  };
}
